import React, { useState } from 'react'

import {
  AlignItems,
  Body2,
  Box,
  Button,
  ButtonColorVariant,
  ButtonSize,
  ButtonVariant,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Heading2,
  Icon,
  IconVariant,
  JustifyContent,
  ModalButton,
  Overflow,
  Space,
  Spinner,
} from '@gousto-internal/citrus-react'
import Modal from 'react-modal'

import type {
  LandingExperienceAnalytics,
  LandingExperienceData,
} from '@library/api-customer-engagement-experience'
import { queueTrackingEvent } from '@library/tracking-integration'

import { HeroPanel } from './components/HeroPanel'
import { MobileHero } from './components/MobileHero'
import { OptionCard } from './components/OptionCard'
import { useGoustoAnytimeIntentHandler } from './useGoustoAnytimeIntentHandler'

type GoustoAnytimeModalProps = {
  data: LandingExperienceData
  onDismiss: () => void
}

const modalOverlayStyle: React.CSSProperties = {
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 2001,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
}

const modalContentStyle: React.CSSProperties = {
  position: 'relative',
  inset: 'initial',
  padding: 0,
  border: 'none',
  background: 'transparent',
  maxWidth: 'none',
  width: 'auto',
  maxHeight: '92vh',
  overflow: 'auto',
  overscrollBehavior: 'contain',
  WebkitOverflowScrolling: 'touch',
}

export const GoustoAnytimeModal = ({ data, onDismiss }: GoustoAnytimeModalProps) => {
  const { onAction } = useGoustoAnytimeIntentHandler()
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null)
  const [isStartingOneOffOrder, setIsStartingOneOffOrder] = useState(false)

  const selectedOption = data.options?.find((option) => option.id === selectedOptionId)
  const continueLabel = selectedOption?.action.title ?? ''

  const handleContinue = async () => {
    if (!selectedOption || isStartingOneOffOrder) return

    if (selectedOption.action.analytics) {
      queueTrackingEvent<LandingExperienceAnalytics>(selectedOption.action.analytics)
    }

    setIsStartingOneOffOrder(true)
    try {
      await onAction(selectedOption.action.intent)
    } finally {
      setIsStartingOneOffOrder(false)
    }
  }

  // Tracked only on the explicit defer CTA; the X and overlay close intentionally stay untracked.
  const handleDefer = () => {
    if (data.defer.analytics) {
      queueTrackingEvent<LandingExperienceAnalytics>(data.defer.analytics)
    }

    onDismiss()
  }

  return (
    <Modal
      isOpen
      appElement={document.getElementById('react-root') ?? undefined}
      onRequestClose={onDismiss}
      style={{ overlay: modalOverlayStyle, content: modalContentStyle }}
      contentLabel={data.heading}
      shouldCloseOnOverlayClick
    >
      <Box
        display={Display.Flex}
        flexDirection={[FlexDirection.Column, null, FlexDirection.Row]}
        alignItems={AlignItems.Stretch}
        width={[null, null, '818px']}
        minWidth={['334px', null, null]}
        maxWidth={['480px', null, '90vw']}
        color={Color.White}
        overflowX={Overflow.Hidden}
        overflowY={Overflow.Hidden}
      >
        <MobileHero message={data.hero.welcomeMobile} onDismiss={onDismiss} />
        <HeroPanel hero={data.hero} />

        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          paddingH={6}
          paddingV={6}
          bg={Color.White}
          flexGrow={1}
          gap={6}
        >
          <Box
            display={Display.Flex}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.SpaceBetween}
          >
            <Heading2 color={Color.ColdGrey_900} fontWeight={FontWeight.Bold} size={[7, null, 9]}>
              {data.heading}
            </Heading2>
            <Box display={[Display.None, null, Display.Flex]} alignItems={AlignItems.Center}>
              <Button
                aria-label="Close"
                colorVariant={ButtonColorVariant.Tertiary}
                icon="close"
                onClick={onDismiss}
                variant={ButtonVariant.Icon}
              />
            </Box>
          </Box>

          <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={4}>
            {data.banner && (
              <Box
                data-testid="gousto-anytime-banner"
                role="region"
                aria-label="Promotion"
                paddingV={4}
                paddingH={4}
                bg={Color.Success_50}
                display={Display.Flex}
                alignItems={AlignItems.FlexStart}
                style={{ borderRadius: '8px' }}
              >
                <Icon name="offer_percentage" variant={IconVariant.Confirmation} size={6} />
                <Space direction="horizontal" size={2} />
                <Box>
                  <Body2 size={4} color={Color.Success_900} fontWeight={FontWeight.Bold}>
                    {data.banner.title}
                  </Body2>
                  <Body2 size={4} color={Color.ColdGrey_900}>
                    {data.banner.subtitle}
                  </Body2>
                </Box>
              </Box>
            )}

            <Box
              role="radiogroup"
              display={Display.Flex}
              flexDirection={FlexDirection.Column}
              gap={4}
            >
              {data.options?.map((option) => (
                <OptionCard
                  key={option.id}
                  option={option}
                  isSelected={selectedOptionId === option.id}
                  onSelect={() => setSelectedOptionId(option.id)}
                />
              ))}
            </Box>
          </Box>

          <Box display={Display.Flex} flexDirection={FlexDirection.Column} width="100%" gap={2}>
            <ModalButton
              data-testid="gousto-anytime-continue"
              colorVariant={ButtonColorVariant.Primary}
              size={ButtonSize.Large}
              onClick={handleContinue}
              disabled={!selectedOptionId || isStartingOneOffOrder}
              height="48px"
              width="100%"
              style={{
                borderRadius: '8px',
                visibility: selectedOptionId ? 'visible' : 'hidden',
              }}
            >
              {isStartingOneOffOrder ? <Spinner color={Color.White} size={6} /> : continueLabel}
            </ModalButton>
            <ModalButton
              colorVariant={ButtonColorVariant.Tertiary}
              size={ButtonSize.Large}
              onClick={handleDefer}
              disabled={isStartingOneOffOrder}
              height="48px"
              style={{
                borderRadius: '8px',
                alignSelf: 'center',
                width: 'fit-content',
              }}
            >
              {data.defer.title}
            </ModalButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}
